import { useMemo, useState } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { RouteBackendOverrides } from "@/api/admin-api"

import { updateRoutesStatusBatch } from "@/api/admin-api"
import { Route, Shield, ShieldCheck } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import { filterModuleRoutes } from "@/contexts/module-route-utils"
import { filterHostedAgentsRoutes } from "@/contexts/route-object-utils"
import { useHostedAgentsEnabled } from "@/hooks/use-config-query"

import { AccessPolicyDialog } from "./access-policy-dialog"
import { CollapsibleManagementSection } from "./collapsible-management-section"
import { DelegationModeControl } from "./delegation-mode-control"
import { EditRouteDialog } from "./edit-route-dialog"
import { RouteBackendOverridesDialog } from "./route-backend-overrides-dialog"
import { RoutesTable } from "./routes-table"
import {
  useAdminRouteAccessPolicyQuery,
  useAdminRouteBaseAccessPolicyQuery,
  useAdminRoutesQuery,
  useClearRouteAccessPolicyMutation,
  useRouteBackendOverridesQuery,
  useUpdateRouteAccessPolicyMutation,
  useUpdateRouteBackendOverridesMutation,
  useUpdateRouteBaseAccessPolicyMutation,
  useUpdateRouteStatusMutation,
} from "../hooks/admin-queries"
import { isPolicyConfigured } from "../utils/access-policy"

export function RoutesManagementCard() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [updatingRouteId, setUpdatingRouteId] = useState<string | undefined>()
  const [isUpdatingAll, setIsUpdatingAll] = useState(false)
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false)
  const [accessRouteId, setAccessRouteId] = useState<string | undefined>()
  const [isBaseAccessDialogOpen, setIsBaseAccessDialogOpen] = useState(false)
  const [settingsRouteId, setSettingsRouteId] = useState<string | undefined>()
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false)
  const [editingRouteId, setEditingRouteId] = useState<string | null>(null)

  const { data: allRoutes, isLoading } = useAdminRoutesQuery()
  // Hosted agents are on-prem only: where the deployment switch is off the
  // Agents module is not offered here either (nor flipped by "enable all").
  const hostedAgentsEnabled = useHostedAgentsEnabled()
  const routes = useMemo(
    () =>
      allRoutes
        ? filterModuleRoutes(filterHostedAgentsRoutes(allRoutes, hostedAgentsEnabled))
        : allRoutes,
    [allRoutes, hostedAgentsEnabled]
  )
  const updateMutation = useUpdateRouteStatusMutation()
  const updateAccessMutation = useUpdateRouteAccessPolicyMutation()
  const clearAccessMutation = useClearRouteAccessPolicyMutation()
  const updateBackendOverridesMutation = useUpdateRouteBackendOverridesMutation()
  const {
    data: baseAccessPolicy,
    isLoading: isLoadingBaseAccessPolicy,
    isError: isBaseAccessPolicyError,
  } = useAdminRouteBaseAccessPolicyQuery(true)
  const updateBaseAccessMutation = useUpdateRouteBaseAccessPolicyMutation()
  const {
    data: routeAccessPolicy,
    isLoading: isLoadingAccessPolicy,
    isError: isAccessPolicyError,
  } = useAdminRouteAccessPolicyQuery(accessRouteId, isAccessDialogOpen)
  const { data: routeBackendOverrides, isLoading: isLoadingBackendOverrides } =
    useRouteBackendOverridesQuery(settingsRouteId, isSettingsDialogOpen)

  const handleToggleStatus = async (routeId: string, enabled: boolean) => {
    setUpdatingRouteId(routeId)
    try {
      await updateMutation.mutateAsync({ routeId, enabled })
    } catch {
      // Error handled by React Query mutation
    } finally {
      setUpdatingRouteId(undefined)
    }
  }

  const handleToggleAll = async (enabled: boolean) => {
    if (!routes || routes.length === 0) return

    setIsUpdatingAll(true)

    // Cancel any pending queries to prevent them from overwriting our optimistic update
    await queryClient.cancelQueries({ queryKey: ["admin", "routes"] })

    // Get fresh data from cache instead of relying on potentially stale prop
    const currentRoutes = queryClient.getQueryData<typeof routes>(["admin", "routes"]) || routes

    // Optimistically update all routes at once
    const previousRoutes = currentRoutes
    const routesToUpdate = filterModuleRoutes(
      filterHostedAgentsRoutes(currentRoutes, hostedAgentsEnabled)
    ).filter((route) => route.enabled !== enabled)

    // Update UI immediately
    queryClient.setQueryData(
      ["admin", "routes"],
      currentRoutes.map((route) =>
        routesToUpdate.some((candidate) => candidate.id === route.id)
          ? { ...route, enabled }
          : route
      )
    )

    try {
      // Use batch API to update all routes in a single operation (avoids Cosmos DB race conditions)
      const updates = Object.fromEntries(routesToUpdate.map((route) => [route.id, enabled]))
      await updateRoutesStatusBatch(updates)
    } catch {
      // Error handled by React Query mutation
      // Rollback optimistic update on error
      queryClient.setQueryData(["admin", "routes"], previousRoutes)
      // Refetch on error to ensure consistency
      await queryClient.invalidateQueries({ queryKey: ["admin", "routes"] })
    } finally {
      setIsUpdatingAll(false)
    }
  }

  // Determine toggle state: true if all enabled, false if all disabled, undefined if mixed
  const allEnabledState = useMemo(() => {
    if (!routes || routes.length === 0) return false
    const enabledCount = routes.filter((route) => route.enabled).length
    if (enabledCount === 0) return false
    if (enabledCount === routes.length) return true
    return undefined // Mixed state
  }, [routes])

  const selectedRoute = routes?.find((r) => r.id === accessRouteId)
  const selectedSettingsRoute = routes?.find((r) => r.id === settingsRouteId)
  const inheritByDefault = selectedRoute?.access_overridden !== true

  const baseAccessConfigured = useMemo(() => {
    return isPolicyConfigured(baseAccessPolicy)
  }, [baseAccessPolicy])

  return (
    <>
      <CollapsibleManagementSection
        icon={<Route className="size-5" />}
        id="routes"
        storageKey="admin-management-routes-expanded"
        title={t("adminDashboard.routes.title", "Route Management")}
        headerActions={
          <div className="flex items-center gap-4">
            <Button
              className="flex items-center gap-2 px-3"
              disabled={isLoading || isUpdatingAll}
              size="sm"
              type="button"
              variant="outline"
              onClick={() => setIsBaseAccessDialogOpen(true)}
            >
              {baseAccessConfigured ? (
                <ShieldCheck className="text-success size-4" />
              ) : (
                <Shield className="size-4" />
              )}
              {t("adminDashboard.routes.baseAccess", { defaultValue: "Access for all" })}
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground text-sm">
                {t("adminDashboard.routes.enableAll", "Enable All")}
              </span>
              <Switch
                aria-label={t("adminDashboard.routes.enableAll", "Enable All")}
                checked={allEnabledState === true}
                disabled={isLoading || isUpdatingAll || !routes || routes.length === 0}
                onCheckedChange={handleToggleAll}
              />
            </div>
            <DelegationModeControl capability="routes" />
          </div>
        }
      >
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg border p-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-12" />
              </div>
            ))}
          </div>
        ) : (
          <RoutesTable
            disableAll={isUpdatingAll}
            isUpdating={updatingRouteId}
            routes={routes || []}
            onEdit={(routeId) => setEditingRouteId(routeId)}
            onToggleStatus={handleToggleStatus}
            onEditAccess={(routeId) => {
              setAccessRouteId(routeId)
              setIsAccessDialogOpen(true)
            }}
            onEditSettings={(routeId) => {
              setSettingsRouteId(routeId)
              setIsSettingsDialogOpen(true)
            }}
          />
        )}
      </CollapsibleManagementSection>

      <EditRouteDialog
        isOpen={editingRouteId !== null}
        route={routes?.find((r) => r.id === editingRouteId) ?? null}
        onOpenChange={(open) => {
          if (!open) setEditingRouteId(null)
        }}
      />

      <AccessPolicyDialog
        isLoading={isLoadingBaseAccessPolicy}
        isSaving={updateBaseAccessMutation.isPending}
        loadError={isBaseAccessPolicyError}
        open={isBaseAccessDialogOpen}
        policy={baseAccessPolicy}
        description={t("adminDashboard.routes.baseAccessDescription", {
          defaultValue: "Default access policy applied to all routes unless overridden per route.",
        })}
        title={t("adminDashboard.routes.baseAccessTitle", {
          defaultValue: "Base access policy — Routes",
        })}
        onOpenChange={setIsBaseAccessDialogOpen}
        onSave={async (policy) => {
          try {
            await updateBaseAccessMutation.mutateAsync({ policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <AccessPolicyDialog
        isLoading={isLoadingAccessPolicy}
        isSaving={updateAccessMutation.isPending || clearAccessMutation.isPending}
        loadError={isAccessPolicyError || isBaseAccessPolicyError}
        open={isAccessDialogOpen}
        policy={routeAccessPolicy}
        description={t("adminDashboard.access.dialogDescription", {
          defaultValue: "Configure who can access this resource.",
        })}
        inherit={{
          enabled: true,
          inheritByDefault,
          policy: baseAccessPolicy,
          label: t("adminDashboard.routes.inheritBaseAccess", {
            defaultValue: "Inherit access for all",
          }),
          description: t("adminDashboard.routes.inheritBaseAccessDescription", {
            defaultValue:
              "When enabled, this route uses the section baseline access policy. Disable to override for this route.",
          }),
          onSaveInherited: async () => {
            if (!accessRouteId) return
            await clearAccessMutation.mutateAsync({ routeId: accessRouteId })
            void queryClient.invalidateQueries({ queryKey: ["admin", "routes"] })
          },
        }}
        title={t("adminDashboard.access.dialogTitle", {
          defaultValue: "Access policy — {{name}}",
          name: selectedRoute?.name ?? accessRouteId ?? "",
        })}
        onOpenChange={(open) => {
          setIsAccessDialogOpen(open)
          if (!open) {
            setAccessRouteId(undefined)
          }
        }}
        onSave={async (policy) => {
          if (!accessRouteId) return
          try {
            await updateAccessMutation.mutateAsync({ routeId: accessRouteId, policy })
            toast.success(
              t("adminDashboard.access.saveSuccess", { defaultValue: "Access policy updated" })
            )
          } catch (error) {
            toast.error(
              t("adminDashboard.access.saveError", {
                defaultValue: "Failed to update access policy",
              })
            )
            throw error
          }
        }}
      />

      <RouteBackendOverridesDialog
        isLoading={isLoadingBackendOverrides}
        isOpen={isSettingsDialogOpen}
        isSaving={updateBackendOverridesMutation.isPending}
        overrides={routeBackendOverrides}
        routeId={settingsRouteId}
        routeName={selectedSettingsRoute?.name ?? settingsRouteId ?? ""}
        onOpenChange={(open) => {
          setIsSettingsDialogOpen(open)
          if (!open) {
            setSettingsRouteId(undefined)
          }
        }}
        onSave={async (overrides: RouteBackendOverrides) => {
          if (!settingsRouteId) return
          try {
            await updateBackendOverridesMutation.mutateAsync({
              routeId: settingsRouteId,
              overrides,
            })
            toast.success(
              t("adminDashboard.routes.deepSettings.saveSuccess", {
                defaultValue: "Route backend settings updated",
              })
            )
            setIsSettingsDialogOpen(false)
            setSettingsRouteId(undefined)
          } catch (error) {
            toast.error(
              t("adminDashboard.routes.deepSettings.saveError", {
                defaultValue: "Failed to update route backend settings",
              })
            )
            throw error
          }
        }}
      />
    </>
  )
}
